import React, { useState, useEffect } from 'react';
import styles from './DashboardStatsCards.module.css';

// 타입 정의
interface StatCardData {
  title: string;
  value: number;
  unit: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
}

interface DashboardStats {
  newMembers: StatCardData;
  activeMembers: StatCardData;
  courseStarts: StatCardData;
  averageWatchTime: { minutes: number; seconds: number; change: string; changeType: 'positive' | 'negative' | 'neutral' };
  newQuestions: StatCardData;
}

const DashboardStatsCards: React.FC = () => {
  const [statsData, setStatsData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  console.log('🎯 DashboardStatsCards 렌더링');

  const fetchDashboardData = async (): Promise<DashboardStats> => {
    console.log('🚀 API 데이터 로딩 시작');
    
    try {
      const responses = await Promise.all([
        fetch('https://campus-dev.duckdns.org/api/admin/new-member-count/today'),
        fetch('https://campus-dev.duckdns.org/api/admin/active-member-count/today'),
        fetch('https://campus-dev.duckdns.org/api/admin/average-watch-time/today'),
        fetch('https://campus-dev.duckdns.org/api/admin/new-questions-count/today'),
        fetch('https://campus-dev.duckdns.org/api/admin/course-start-count?startDate=2025-08-25&endDate=2025-08-25')
      ]);

      const data = await Promise.all(responses.map(r => r.json()));
      console.log('✅ API 데이터:', data);

      // 변화량 계산
      const calculateChange = (today: number, yesterday: number) => {
        if (yesterday === 0) {
          if (today === 0) return { change: '0%', type: 'neutral' as const };
          return { change: '+100%', type: 'positive' as const };
        }
        const pct = ((today - yesterday) / yesterday) * 100;
        if (pct > 0) return { change: `+${pct.toFixed(1)}%`, type: 'positive' as const };
        if (pct < 0) return { change: `${pct.toFixed(1)}%`, type: 'negative' as const };
        return { change: '0%', type: 'neutral' as const };
      };

      // 데이터 파싱
      const newMembersToday = data[0]?.data?.today || 0;
      const newMembersYesterday = data[0]?.data?.yesterday || 0;
      const activeMembersToday = data[1]?.data?.today || 0;
      const activeMembersYesterday = data[1]?.data?.yesterday || 0;
      const watchTimeToday = data[2]?.data?.today || 0;
      const watchTimeYesterday = data[2]?.data?.yesterday || 0;
      const questionsToday = data[3]?.data?.today || 0;
      const questionsYesterday = data[3]?.data?.yesterday || 0;
      const courseStartsToday = data[4]?.data?.[0]?.count || 0;

      // 시간 변환 (분 -> 분초)
      const minutes = Math.floor(watchTimeToday);
      const seconds = Math.round((watchTimeToday - minutes) * 60);

      // 변화량 계산
      const newMembersChange = calculateChange(newMembersToday, newMembersYesterday);
      const activeMembersChange = calculateChange(activeMembersToday, activeMembersYesterday);
      const watchTimeChange = calculateChange(watchTimeToday, watchTimeYesterday);
      const questionsChange = calculateChange(questionsToday, questionsYesterday);
      const courseStartsChange = calculateChange(courseStartsToday, 0);

      return {
        newMembers: {
          title: '신규 가입자 수',
          value: newMembersToday,
          unit: '명',
          change: newMembersChange.change,
          changeType: newMembersChange.type
        },
        activeMembers: {
          title: '일일 활성 사용자 수',
          value: activeMembersToday,
          unit: '명',
          change: activeMembersChange.change,
          changeType: activeMembersChange.type
        },
        courseStarts: {
          title: '수강 시작 수',
          value: courseStartsToday,
          unit: '회',
          change: courseStartsChange.change,
          changeType: courseStartsChange.type
        },
        averageWatchTime: {
          minutes,
          seconds,
          change: watchTimeChange.change,
          changeType: watchTimeChange.type
        },
        newQuestions: {
          title: '신규 등록 질문 수',
          value: questionsToday,
          unit: '개',
          change: questionsChange.change,
          changeType: questionsChange.type
        }
      };
    } catch (error) {
      console.error('❌ API 에러:', error);
      throw error;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchDashboardData();
        setStatsData(data);
      } catch (error) {
        console.error('데이터 로딩 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className={styles.statsContainer}>
        {/* 로딩 중일 때 원래 UI 구조 유지 */}
        {[...Array(5)].map((_, index) => (
          <div key={index} className={styles.card}>
            <div className={styles.heading}>
              <div className={styles.title}>로딩 중...</div>
            </div>
            <div className={styles.body}>
              <b className={styles.number}>--</b>
              <div className={styles.unit}>--</div>
            </div>
            <div className={styles.trailing}>
              <div className={styles.change}>--%</div>
              <div className={styles.period}>전일 대비</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!statsData) {
    return <div>데이터를 불러올 수 없습니다.</div>;
  }

  return (
    <div className={styles.statsContainer}>
      {/* 신규 가입자 수 */}
      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.title}>{statsData.newMembers.title}</div>
        </div>
        <div className={styles.body}>
          <b className={styles.number}>{statsData.newMembers.value.toLocaleString()}</b>
          <div className={styles.unit}>{statsData.newMembers.unit}</div>
        </div>
        <div className={`${styles.trailing} ${statsData.newMembers.changeType === 'negative' ? styles.negative : ''}`}>
          <div className={styles.change}>{statsData.newMembers.change}</div>
          <div className={styles.period}>전일 대비</div>
        </div>
      </div>

      {/* 활성 사용자 수 */}
      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.title}>{statsData.activeMembers.title}</div>
        </div>
        <div className={styles.body}>
          <b className={styles.number}>{statsData.activeMembers.value.toLocaleString()}</b>
          <div className={styles.unit}>{statsData.activeMembers.unit}</div>
        </div>
        <div className={`${styles.trailing} ${statsData.activeMembers.changeType === 'negative' ? styles.negative : ''}`}>
          <div className={styles.change}>{statsData.activeMembers.change}</div>
          <div className={styles.period}>전일 대비</div>
        </div>
      </div>

      {/* 수강 시작 수 */}
      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.title}>{statsData.courseStarts.title}</div>
        </div>
        <div className={styles.body}>
          <b className={styles.number}>{statsData.courseStarts.value.toLocaleString()}</b>
          <div className={styles.unit}>{statsData.courseStarts.unit}</div>
        </div>
        <div className={`${styles.trailing} ${statsData.courseStarts.changeType === 'negative' ? styles.negative : ''}`}>
          <div className={styles.change}>{statsData.courseStarts.change}</div>
          <div className={styles.period}>전일 대비</div>
        </div>
      </div>

      {/* 평균 시청 시간 */}
      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.title}>평균 시청 시간</div>
        </div>
        <div className={styles.bodyTime}>
          <div className={styles.timeUnit}>
            <b className={styles.number}>{String(statsData.averageWatchTime.minutes).padStart(2, '0')}</b>
            <div className={styles.unit}>분</div>
          </div>
          <div className={styles.timeUnit}>
            <b className={styles.number}>{String(statsData.averageWatchTime.seconds).padStart(2, '0')}</b>
            <div className={styles.unit}>초</div>
          </div>
        </div>
        <div className={`${styles.trailing} ${statsData.averageWatchTime.changeType === 'negative' ? styles.negative : ''}`}>
          <div className={styles.change}>{statsData.averageWatchTime.change}</div>
          <div className={styles.period}>전일 대비</div>
        </div>
      </div>

      {/* 신규 등록 질문 수 */}
      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.title}>{statsData.newQuestions.title}</div>
        </div>
        <div className={styles.body}>
          <b className={styles.number}>{statsData.newQuestions.value.toLocaleString()}</b>
          <div className={styles.unit}>{statsData.newQuestions.unit}</div>
        </div>
        <div className={`${styles.trailing} ${statsData.newQuestions.changeType === 'negative' ? styles.negative : ''}`}>
          <div className={styles.change}>{statsData.newQuestions.change}</div>
          <div className={styles.period}>전일 대비</div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStatsCards;